// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Funds API Client
 *
 * Calls funds-service via the Advisor BFF (/api/funds/**). The BFF forwards
 * X-Tenant-Id (from the tenant subdomain) and the OAuth2 bearer; funds-service
 * resolves the brokerage + advisor from those.
 */

import api from './advisorApi';

export interface FundDTO {
  fundKey: string;
  fundName: string;
  fundId: string | null;
  providerCode: string;
  providerDisplayName: string | null;
  categoryName: string | null;
  launchDate: string | null;
  price: number | null;
  priceDate: string | null;
  performanceDate: string | null;
  factsheetUrl: string | null;
  isOpen: boolean | null;
  perf1mPct: number | null;
  perf3mPct: number | null;
  perf1yPct: number | null;
  perf3yPct: number | null;
  perf5yPct: number | null;
  perf10yPct: number | null;
  perfSinceLaunchPct: number | null;
  calendarYears: Record<string, string> | null;
  favourited: boolean;
  // Canonical risk rating (Tier 2) — null when the provider publishes none.
  riskRating?: number | null;
  riskRatingRaw?: string | null;
  riskRatingSource?: string | null;
  // Coalesced key facts (Fund Details Enrichment Tier 1) — null when the
  // provider doesn't publish them.
  sfdr?: string | null;
  assetClass?: string | null;
  fundManager?: string | null;
  fundSize?: number | null;
  amc?: number | null;
  ocf?: number | null;
  // Verbatim provider overflow — present only on the single-fund detail read.
  extraFields?: Record<string, unknown> | null;
  /**
   * Central-taxonomy tag ids assigned to this fund in this brokerage
   * (Tags PRD §6.4/FR-19). Resolve names/colours against the cached
   * central tag list (`tagsApi.list()`/`useTags()`) — funds-service cannot
   * validate ids against a different database, so an id that doesn't
   * resolve (deleted mid-propagation) should render nothing.
   */
  tagIds: string[];
}

export interface FundSearchResponse {
  content: FundDTO[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface FundSearchParams {
  q?: string;
  provider?: string;
  category?: string;
  onlyOpen?: boolean;
  sort?: string;
  page?: number;
  size?: number;
  /** Central-taxonomy tag ids — ANY-of filter (Tags PRD FR-13); no-op when omitted or the tags feature is off. */
  tagIds?: string[];
}

export interface FavouriteDTO {
  id: string;
  fundKey: string;
  fundName: string | null;
  providerCode: string | null;
  providerDisplayName: string | null;
  categoryName: string | null;
  addedAt: string;
}

/** Fund assignment count for a central taxonomy tag in this brokerage. Mirrors {@code FundTagUsageDTO}. */
export interface FundTagUsageDTO {
  tagId: string;
  count: number;
}

/** One fund allocation within a strategy. Mirrors funds-service {@code StrategyComponentDTO}. */
export interface StrategyComponentDTO {
  fundKey: string;
  allocationPct: number | null;
  sortOrder: number | null;
  /** Canonical 1-7 risk of the component fund; server-derived (read-only). Null when unrated. */
  riskRating?: number | null;
}

/**
 * Multi-fund strategy header + components. Mirrors funds-service
 * {@code StrategyDTO}. Used by the proposal fund pickers to "Load from
 * Strategy" — a strategy's components map 1:1 onto proposal fund lines.
 */
export interface FundStats {
  totalFunds: number;
  providerCount: number;
  openFundsCount: number;
  favouriteCount: number;
  portfolioCount: number;
  lastSyncedAt: string | null;
  lastSyncStatus: string | null;
}

export type StrategyStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type StrategyScope = 'ADVISOR' | 'BROKERAGE';
export type RiskProfile = 'CONSERVATIVE' | 'BALANCED' | 'GROWTH' | 'ADVENTUROUS';

export interface StrategyDTO {
  id: string;
  scope: StrategyScope;
  ownerAdvisorId: string | null;
  name: string;
  description: string | null;
  status: StrategyStatus;
  riskProfile: RiskProfile | null;
  publishedAt: string | null;
  publishedBy: string | null;
  createdAt: string | null;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  components: StrategyComponentDTO[];
}

/** Mirrors funds-service StrategyCreateRequest / StrategyUpdateRequest. */
export interface StrategyUpsertRequest {
  name: string;
  description?: string | null;
  riskProfile?: RiskProfile | null;
  components: StrategyComponentDTO[];
}

export interface StrategyListResponse {
  content: StrategyDTO[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export const fundsApi = {
  search: async (params: FundSearchParams = {}): Promise<FundSearchResponse> => {
    const response = await api.get<FundSearchResponse>('/funds', {
      params: {
        ...params,
        tagIds: params.tagIds?.length ? params.tagIds : undefined,
      },
      // Spring binds List<UUID> from repeated query params (tagIds=a&tagIds=b),
      // not axios's default `tagIds[]=a` bracket notation — same fix as
      // requestOverviewApi.list.
      paramsSerializer: { indexes: null },
    });
    return response.data;
  },

  get: async (fundKey: string): Promise<FundDTO> => {
    const response = await api.get<FundDTO>(`/funds/${encodeURIComponent(fundKey)}`);
    return response.data;
  },

  /** Aggregate counts for the Fund Center stat tiles. */
  getStats: async (): Promise<FundStats> => {
    const response = await api.get<FundStats>('/funds/stats');
    return response.data;
  },

  /** Strategies visible to the advisor (own + BROKERAGE-scope PUBLISHED). Each carries its components. */
  listStrategies: async (page = 0, size = 100): Promise<StrategyListResponse> => {
    const response = await api.get<StrategyListResponse>('/funds/strategies', {
      params: { page, size },
    });
    return response.data;
  },

  /** A single strategy with its components — the "Load from Strategy" seed source. */
  getStrategy: async (id: string): Promise<StrategyDTO> => {
    const response = await api.get<StrategyDTO>(`/funds/strategies/${encodeURIComponent(id)}`);
    return response.data;
  },

  /** Create a DRAFT portfolio (strategy). */
  createStrategy: async (req: StrategyUpsertRequest): Promise<StrategyDTO> => {
    const response = await api.post<StrategyDTO>('/funds/strategies', req);
    return response.data;
  },

  /** Edit a DRAFT portfolio. */
  updateStrategy: async (id: string, req: StrategyUpsertRequest): Promise<StrategyDTO> => {
    const response = await api.put<StrategyDTO>(`/funds/strategies/${encodeURIComponent(id)}`, req);
    return response.data;
  },

  /** DRAFT → PUBLISHED (server validates sum-to-100 and component count). */
  publishStrategy: async (id: string): Promise<StrategyDTO> => {
    const response = await api.post<StrategyDTO>(`/funds/strategies/${encodeURIComponent(id)}/publish`);
    return response.data;
  },

  /** Soft-delete (any status). */
  archiveStrategy: async (id: string): Promise<StrategyDTO> => {
    const response = await api.post<StrategyDTO>(`/funds/strategies/${encodeURIComponent(id)}/archive`);
    return response.data;
  },

  /** Clone into a new DRAFT owned by the caller (optionally renamed). */
  cloneStrategy: async (id: string, name?: string): Promise<StrategyDTO> => {
    const response = await api.post<StrategyDTO>(
      `/funds/strategies/${encodeURIComponent(id)}/clone`,
      undefined,
      { params: name ? { name } : undefined }
    );
    return response.data;
  },

  /** Hard-delete a DRAFT (server rejects PUBLISHED/ARCHIVED). */
  deleteStrategy: async (id: string): Promise<void> => {
    await api.delete(`/funds/strategies/${encodeURIComponent(id)}`);
  },

  listFavourites: async (page = 0, size = 100): Promise<{ content: FavouriteDTO[] }> => {
    const response = await api.get<{ content: FavouriteDTO[] }>('/funds/favourites', {
      params: { page, size },
    });
    return response.data;
  },

  addFavourite: async (fundKey: string): Promise<FavouriteDTO> => {
    const response = await api.post<FavouriteDTO>('/funds/favourites', { fundKey });
    return response.data;
  },

  removeFavourite: async (fundKey: string): Promise<void> => {
    await api.delete(`/funds/favourites/${encodeURIComponent(fundKey)}`);
  },

  /**
   * Batch tag decoration for a set of funds (one call per list page — see
   * `FundTagController.getForFundKeys`). Returns fundKey -> assigned tagIds;
   * resolve names/colours against the cached central tag list.
   */
  getTagsForFundKeys: async (fundKeys: string[]): Promise<Record<string, string[]>> => {
    if (fundKeys.length === 0) return {};
    const response = await api.get<Record<string, string[]>>('/funds/tags', {
      params: { fundKeys },
      paramsSerializer: { indexes: null },
    });
    return response.data;
  },

  /** Fund assignment counts for a set of central tag ids — merged client-side with the RS usage endpoint on the admin Tags page (FR-15). */
  getFundTagUsage: async (tagIds: string[]): Promise<FundTagUsageDTO[]> => {
    if (tagIds.length === 0) return [];
    const response = await api.get<FundTagUsageDTO[]>('/funds/tags/usage', {
      params: { tagIds },
      paramsSerializer: { indexes: null },
    });
    return response.data;
  },

  /** Replace a fund's tag set — idempotent, computes the add/remove delta server-side. */
  replaceFundTags: async (fundKey: string, tagIds: string[]): Promise<string[]> => {
    const response = await api.put<string[]>(
      `/funds/${encodeURIComponent(fundKey)}/tags`,
      { tagIds },
    );
    return response.data;
  },

  assignFundTag: async (fundKey: string, tagId: string): Promise<void> => {
    await api.post(`/funds/${encodeURIComponent(fundKey)}/tags/${tagId}`);
  },

  unassignFundTag: async (fundKey: string, tagId: string): Promise<void> => {
    await api.delete(`/funds/${encodeURIComponent(fundKey)}/tags/${tagId}`);
  },
};

export default fundsApi;
